// web/src/copilot/ConversationsList.tsx
//
// Compact dropdown listing the user's past conversations. The trigger button
// shows the currently-selected conversation's title (or "New conversation"),
// and the menu contains "+ New" plus all conversations. This replaces the
// earlier vertical stack which grew unbounded and ate the chat panel's
// vertical space as conversations accumulated.
import { ChevronDownIcon, MessageSquarePlusIcon } from "lucide-react";
import { useConversations } from "./api";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface ConversationsListProps {
  selectedId: string | null;
  onSelect: (id: string) => void;
  onNew: () => void;
}

export function ConversationsList({
  selectedId,
  onSelect,
  onNew,
}: ConversationsListProps) {
  const { data, isPending } = useConversations();
  const conversations = data?.conversations ?? [];

  const selected = conversations.find((c) => c.id === selectedId);
  const triggerLabel = selected
    ? (selected.title || "Untitled")
    : "New conversation";

  return (
    <div className="border-b border-border pb-2 mb-2 flex items-center gap-2">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className="flex-1 justify-between font-normal"
          >
            <span className="truncate">{triggerLabel}</span>
            <ChevronDownIcon className="ms-2 size-4 shrink-0 opacity-50" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-(--radix-dropdown-menu-trigger-width)">
          <DropdownMenuItem onSelect={onNew}>
            <MessageSquarePlusIcon className="size-4" />
            New conversation
          </DropdownMenuItem>

          {(conversations.length > 0 || isPending) && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-xs uppercase tracking-wide text-muted-foreground">
                Previous
              </DropdownMenuLabel>
            </>
          )}

          {isPending && (
            <DropdownMenuItem disabled>Loading…</DropdownMenuItem>
          )}

          {!isPending && conversations.length === 0 && (
            <DropdownMenuItem disabled>No conversations yet</DropdownMenuItem>
          )}

          {conversations.map((c) => (
            <DropdownMenuItem
              key={c.id}
              onSelect={() => onSelect(c.id)}
              className={selectedId === c.id ? "bg-muted" : ""}
            >
              <span className="truncate flex-1">{c.title || "Untitled"}</span>
              <span className="ms-2 text-xs text-muted-foreground shrink-0">
                {c.message_count}
              </span>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
